import { MapPin, MapPinOff, Crosshair } from "lucide-react";

// A GPS fix from an attendance punch (§10.7). Latitude and longitude get their
// own labelled chips rather than a bare "12.97, 77.61" pair — at a glance in a
// dense table the two numbers are otherwise indistinguishable, and a sign or a
// swapped pair is exactly the kind of thing you want to catch by eye.
//
// The whole block links to a map, so a coordinate is something you can act on
// rather than just read.

const MAPS_URL = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;

function Chip({ label, value, size }) {
  const compact = size === "sm";
  return (
    <span
      className={`inline-flex items-baseline gap-1 rounded border border-emerald-200/70 bg-emerald-50 ${
        compact ? "px-1.5 py-[1px]" : "px-2 py-0.5"
      }`}
    >
      <span
        className={`font-semibold uppercase tracking-wider text-emerald-700/70 ${
          compact ? "text-[9px]" : "text-[10px]"
        }`}
      >
        {label}
      </span>
      <span
        className={`font-mono font-semibold tabular-nums text-gray-800 ${
          compact ? "text-[10px]" : "text-xs"
        }`}
      >
        {value}
      </span>
    </span>
  );
}

/**
 * @param location  the punch's `location` block, or null for a manual entry
 * @param size      "sm" inside a table row, "md" in a modal
 * @param showMeta  whether to append accuracy and site proximity
 */
export default function Coordinates({ location, size = "sm", showMeta = true }) {
  if (!location || location.latitude == null || location.longitude == null) {
    return (
      <span className="text-[10px] italic text-gray-400">no GPS fix — entered from the panel</span>
    );
  }

  const { latitude, longitude, accuracy, isWithinSite, distanceMeters } = location;
  const lat = Number(latitude).toFixed(5);
  const lng = Number(longitude).toFixed(5);

  return (
    <span className="mt-1 flex flex-wrap items-center gap-1">
      <a
        href={MAPS_URL(latitude, longitude)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        title={`Open ${lat}, ${lng} in Google Maps`}
        className="inline-flex flex-wrap items-center gap-1 rounded transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
      >
        <Chip label="Lat" value={lat} size={size} />
        <Chip label="Long" value={lng} size={size} />
      </a>

      {showMeta && accuracy != null && (
        <span
          className="inline-flex items-center gap-0.5 text-[10px] text-gray-400"
          title={`Device reported ±${Math.round(accuracy)}m accuracy`}
        >
          <Crosshair size={9} />±{Math.round(accuracy)}m
        </span>
      )}

      {/* Proximity is only computed when the branch has a saved location.geo.
          Null means "never checked", which must not read as "on site". */}
      {showMeta && isWithinSite === true && (
        <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600">
          <MapPin size={9} /> on site
        </span>
      )}
      {showMeta && isWithinSite === false && (
        <span
          className="inline-flex items-center gap-0.5 text-[10px] font-medium text-red-600"
          title="Outside the branch's site radius"
        >
          <MapPinOff size={9} /> {Math.round(distanceMeters ?? 0)}m away
        </span>
      )}
      {showMeta && isWithinSite == null && (
        <span
          className="inline-flex items-center gap-0.5 text-[10px] text-gray-400"
          title="No site location saved on this branch, so proximity was never checked. Set the branch's location to flag off-site punches."
        >
          <MapPin size={9} /> unverified
        </span>
      )}
    </span>
  );
}
